import { computed, readonly, watch, watchEffect, type DeepReadonly } from "vue";
import { defineStore } from "pinia";
import { type Me } from "@/generated/graphql";
import { useTrackingStore } from "@/stores/tracking.store";
import { getLanguageFromLocale } from "@/utils/locale";
import { useAuthStore } from "./auth/auth.store";
import { useCleanupCache } from "@/composables/global/use-cleanup-cache";
import type { Ref, UnwrapNestedRefs } from "vue";
import { graphql as gql } from "@/generated";
import type { ResultOf } from "@graphql-typed-document-node/core";
import { useMutation, useQuery } from "@urql/vue";

const _UserStoreMeFragment = gql(/* GraphQL */ `
  fragment UserStoreMe on Me {
    id
    created_at
    updated_at
    username
    is_verified
    locale
    phone_number
    email
    avatar
    country_iso
    send_email_notifications
    send_show_emails
    send_thread_emails
    send_contest_emails
    send_marketing_emails
    send_sms_notifications
    send_whatsapp_notifications
    sms_consent_decided_at
    whatsapp_consent_decided_at
    email_consent_decided_at
    email_consent_snoozed_until
    date_of_birth
    auto_translate_content
    is_system_admin
    ccpa_do_not_sell
    is_minor
    web_three_wallets {
      address
      chain_id
      user_web_three_wallet_token_balances {
        web_three_erc_twenty_token_address
        balance
      }
    }
  }
`);

const MeDocument = gql(/* GraphQL */ `
  query Me {
    me {
      ... on Me {
        ...UserStoreMe
      }
      ... on UnauthenticatedError {
        error
      }
      ... on InvalidTokenError {
        error
      }
    }
  }
`);

const TrackUserActionDocument = gql(/* GraphQL */ `
  mutation TrackUserAction($input: TrackUserActionInput!) {
    trackUserAction(input: $input) {
      ... on TrackUserActionSuccess {
        success
      }
      ... on InvalidTrackingActionError {
        error
        app_action_name
        allowed_actions
      }
    }
  }
`);

export enum UserAuthenticationStatus {
  Unauthenticated,
  AuthenticatedWithExternalService,
  Authenticated,
}

export type UserStoreMe = DeepReadonly<UnwrapNestedRefs<Me>>;
export type UserStoreMeUnwrapped = UserStoreMe | null;

export const useUserStore = defineStore("user", () => {
  const authStore = useAuthStore();
  const trackingService = useTrackingStore();

  const authStatus = computed(() => {
    if (authStore.hasAuthToken) return UserAuthenticationStatus.Authenticated;
    if (authStore.authMe)
      return UserAuthenticationStatus.AuthenticatedWithExternalService;

    return UserAuthenticationStatus.Unauthenticated;
  });

  const { data: localUserResult, executeQuery: executeMeQuery } = useQuery({
    query: MeDocument,
    variables: {},
    pause: () => authStore.hasAuthToken === false && authStore.authMe === null,
  });

  const localUser = computed(() => {
    const result = localUserResult.value?.me;
    if (!result || "error" in result) return null;
    return result as ResultOf<typeof _UserStoreMeFragment>;
  });
  const user = computed(() => localUser.value ?? authStore.authMe ?? null);
  const userId = computed(() => user.value?.id);

  const userLocale = computed(
    () => user.value?.locale ?? window.navigator.language ?? "en-US",
  );
  const userLanguage = computed(() => getLanguageFromLocale(userLocale.value));

  const avatarPlaceholder = computed(() => {
    const letter = user.value?.username?.charAt(0);
    if (!letter) return "";
    return letter.toUpperCase();
  });
  const username = computed(() => {
    if (user.value?.username) return user.value.username;
    else return `Fan ${Math.floor(Math.random() * 10000)}`;
  });

  const isGuest = computed(() => !user.value && username.value.length > 0);
  const isAuthenticated = computed(
    () => authStatus.value === UserAuthenticationStatus.Authenticated,
  );
  const isAuthenticatedWithExternalService = computed(
    () =>
      authStatus.value ===
      UserAuthenticationStatus.AuthenticatedWithExternalService,
  );

  async function refetchLocalUser() {
    // network-only so this bypasses URQL's document cache. Used after
    // mutations that mutate Me-derived state (connectWallet, disconnectWallet,
    // updateMe, consent toggles, etc.) — without this, the document-cached
    // result is returned and the UI keeps the stale wallet state.
    const result = await executeMeQuery({ requestPolicy: "network-only" });
    const meResult = result.data.value?.me;

    if (
      meResult &&
      "error" in meResult &&
      meResult.error === "InvalidTokenError"
    ) {
      authStore.logout();
    }
  }

  watchEffect(() => {
    const authMe = authStore.authMe;
    const hasAuthToken = authStore.hasAuthToken;

    if (hasAuthToken && !authMe) {
      refetchLocalUser();
    }
  });

  watch(localUser, (value) => {
    if (value) {
      trackingService.identifyUser(value);
    }
  });

  const { cleanupCache } = useCleanupCache();

  const login = () => authStore.login();
  const logout = async () => {
    authStore.logout();
    trackingService.resetIdentity();
    localUserResult.value = undefined;
    await cleanupCache();
  };

  const isSystemAdmin = computed(() => user.value?.is_system_admin ?? false);

  function updateUser(updates: Partial<Me>) {
    if (!user.value) {
      console.error(
        "[UserStore] updateUser called with no user, ignoring updates",
        updates,
      );
      return;
    }

    refetchLocalUser();
  }

  function $reset() {
    localUserResult.value = undefined;
  }

  // Analytics action tracking
  const { executeMutation: executeTrackUserAction } = useMutation(
    TrackUserActionDocument,
  );

  /**
   * Track an analytics action to the backend.
   * This is used for tracking wallet actions and other user interactions.
   *
   * @param appActionName - The action name (must be in TRACKING_ACTIONS on the backend)
   * @param objectId - The object identifier (e.g., wallet address)
   * @param actionMetadata - Additional metadata for the action
   * @returns Promise with success status or error information
   */
  async function trackAnalyticsAction({
    appActionName,
    objectId,
    actionMetadata = {},
  }: {
    appActionName: string;
    objectId?: string | null;
    actionMetadata?: Record<string, unknown>;
  }): Promise<{
    success: boolean;
    error?: string;
    allowedActions?: string[];
  }> {
    if (!isAuthenticated.value) {
      console.warn(
        "[UserStore] trackAnalyticsAction called without authenticated user",
      );
      return { success: false, error: "User not authenticated" };
    }

    try {
      const result = await executeTrackUserAction({
        input: {
          app_action_name: appActionName,
          object_id: objectId ?? null,
          action_metadata: actionMetadata,
        },
      });

      if (result.error) {
        console.error(
          "[UserStore] trackAnalyticsAction error:",
          result.error.message,
        );
        return { success: false, error: result.error.message };
      }

      const response = result.data?.trackUserAction;

      if (response && "error" in response) {
        console.warn(
          "[UserStore] trackAnalyticsAction invalid action:",
          response.error,
          "Allowed actions:",
          response.allowed_actions,
        );
        return {
          success: false,
          error: response.error,
          allowedActions: response.allowed_actions,
        };
      }

      return {
        success: response && "success" in response ? response.success : false,
      };
    } catch (error) {
      console.error("[UserStore] trackAnalyticsAction exception:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  return {
    user: readonly(user) as Ref<UserStoreMeUnwrapped>,
    userId,
    userLocale,
    userLanguage,
    avatarPlaceholder,
    authStatus,
    isAuthenticated,
    isAuthenticatedWithExternalService,
    isSystemAdmin,
    updateUser,
    refetchLocalUser,
    login,
    logout,
    username,
    isGuest,
    $reset,
    trackAnalyticsAction,
  };
});
